<template>
  <div class="movements-toolbar-root">
    <div
      class="bg-red-darken-3 movements-toolbar-strip pt-2 pb-2 pb-md-3 px-3 px-md-6 px-lg-8"
    >
      <v-row
        align="center"
        class="movements-toolbar-search-row flex-wrap"
        no-gutters
      >
        <v-col
          cols="auto"
          class="d-none d-md-flex align-center flex-shrink-0 movements-toolbar-brand ga-3"
        >
          <div class="text-subtitle-1 text-white text-truncate">
            {{ $t("parts.movements.label") }}
          </div>
          <v-divider class="flex-shrink-0" inset vertical />
        </v-col>

        <v-spacer class="d-none d-md-flex" />

        <v-col
          cols="12"
          md="auto"
          class="movements-toolbar-right flex-grow-1 min-width-0"
        >
          <div
            class="d-flex flex-wrap align-center ga-2 ga-md-3 w-100 justify-start justify-md-end"
          >
            <div class="movements-toolbar-fields flex-grow-1 min-width-0">
              <!--SEARCH BY TITLE-->
              <v-autocomplete
                v-if="searchType === 'title'"
                v-model="titleCode"
                v-model:search="searchTitle"
                v-model:menu="menuOpen"
                :loading="loadingTitle"
                :items="titles"
                :label="$t('parts.inventory.search')"
                item-value="partCode"
                :item-title="() => ''"
                :custom-filter="customFilter"
                variant="solo"
                color="grey-darken-4"
                append-inner-icon="mdi-magnify"
                single-line
                hide-details
                hide-no-data
                clearable
                autocomplete="off"
                class="movements-field-on-red"
                @keyup.enter="searchMethod"
                @click:append-inner="searchMethod"
                @update:search="updateSearch"
              >
                <template #item="{ item, props }">
                  <v-list-item
                    v-bind="props"
                    class="listRowColor"
                    @click="selectCode(item)"
                  >
                    <div class="text-start pa-2">
                      <span class="caption">Info Car - </span>
                      <span
                        class="font-weight-bold"
                        v-html="
                          highlight(
                            item?.raw?.title?.[$store.getters.language] ||
                              'No Title',
                            searchTitle,
                          )
                        "
                      />
                    </div>

                    <div
                      v-if="item?.raw?.related?.length"
                      class="related-scroll hidden-sm-and-down pa-2"
                    >
                      <span
                        v-for="(el, idx) in item.raw.related"
                        :key="idx"
                        class="mr-2"
                        style="font-size: 10px"
                        v-html="
                          highlight(
                            el?.title?.[$store.getters.language] ||
                              'No Related Title',
                            searchTitle,
                          )
                        "
                      />
                    </div>
                  </v-list-item>
                </template>
                <template #selection="{ item }">
                  <span v-if="item">
                    {{ item?.raw?.title?.[$store.getters.language] }}
                  </span>
                </template>
              </v-autocomplete>

              <!--SEARCH BY MULTIPLE TITLES-->
              <v-autocomplete
                v-else-if="searchType === 'multipleTitles'"
                v-model="selectedGroup"
                v-model:search="searchMultipleTitles"
                v-model:menu="menuMultipleOpen"
                :loading="loadingMultipleTitles"
                :items="titleGroups"
                :label="$t('parts.movements.search')"
                :item-title="`title.${$store.getters.language}`"
                item-value="_id"
                variant="solo"
                color="grey-darken-4"
                append-inner-icon="mdi-magnify"
                single-line
                hide-details
                hide-no-data
                return-object
                class="movements-field-on-red"
                @update:model-value="onGroupSelected"
                @keyup.enter="searchMethod()"
                @click:append-inner="searchMethod()"
              />

              <!--SEARCH OTHER FIELDS-->
              <v-text-field
                v-else
                v-model="search"
                :label="$t('parts.damaged_parts.search')"
                flat
                variant="solo"
                color="grey-darken-4"
                append-inner-icon="mdi-magnify"
                single-line
                hide-details
                class="movements-field-on-red"
                @keyup.enter="searchMethod()"
                @click:append-inner="searchMethod()"
              />
            </div>

            <v-select
              v-model="searchType"
              :items="searchTypes"
              item-title="label"
              item-value="value"
              color="grey-darken-4"
              variant="solo"
              flat
              hide-details
              class="movements-field-on-red movements-toolbar-type-select flex-shrink-0"
            />

            <v-btn
              color="grey-darken-4"
              size="small"
              variant="flat"
              class="d-none d-md-inline-flex mt-2 mt-md-0 flex-shrink-0 movements-toolbar-advanced-btn"
              @click="advancedSearch = !advancedSearch"
            >
              <v-icon start>
                {{
                  advancedSearch
                    ? "mdi-checkbox-marked-outline"
                    : "mdi-checkbox-blank-outline"
                }}
              </v-icon>
              {{ $t("parts.inventory.advanced") }}
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </div>
    <div class="redColor pa-3 pa-md-4">
      <!-- Mobile: toggle advanced filters (same pattern as vehicle filters) -->
      <div class="d-md-none mb-2">
        <v-btn
          color="grey-darken-4"
          size="small"
          variant="flat"
          block
          @click="toggleMobileAdvanced"
        >
          <v-icon start>
            {{
              mobileAdvancedOpen ? "mdi-chevron-up" : "mdi-chevron-down"
            }}
          </v-icon>
          {{ $t("parts.inventory.advanced") }}
        </v-btn>
      </div>

      <!--------------------------------------ADVANCED SEARCH-------------------------------------->
      <div v-if="showAdvancedPanel" class="movements-advanced-search-panel">
        <v-row
          class="movements-advanced-search-grid pb-2 px-2 px-sm-4 flex-wrap align-center"
        >
          <!----------------------------------------STATUS----------------------------------------->
          <v-col cols="12" md="12" class="pb-0 mb-0">
            <v-select
              v-model="status"
              :items="statusItems"
              :hint="$t('parts.movements.select_parts_status')"
              item-title="title"
              item-value="value"
              variant="solo"
              flat
              chips
              closable-chips
              multiple
              color="red-darken-2"
              class="w-100"
              label="Status"
              persistent-hint
            />
          </v-col>
          <!-----------------------------------------DATE------------------------------------------>
          <!--STARTING DATE-->
          <v-col cols="12" sm="6" md="6">
            <v-menu
              v-model="startDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290"
            >
              <template #activator="{ props }">
                <v-text-field
                  v-model="startDate"
                  :value="formatDateToDisplay(startDate)"
                  :label="$t('parts.advanced_search.starting_date')"
                  :hint="$t('parts.advanced_search.select_starting_date')"
                  variant="solo"
                  flat
                  color="black"
                  persistent-hint
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="props"
                />
              </template>

              <v-date-picker
                v-model="startDate"
                color="red-darken-3"
                :max="endDate || null"
                @update:model-value="startDateMenu = false"
              />
            </v-menu>
          </v-col>
          <!--ENDING DATE-->
          <v-col cols="12" sm="6" md="6">
            <v-menu
              v-model="endDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290"
            >
              <template #activator="{ props }">
                <v-text-field
                  v-model="endDate"
                  :value="formatDateToDisplay(endDate)"
                  :label="$t('parts.advanced_search.ending_date')"
                  :hint="$t('parts.advanced_search.select_ending_date')"
                  variant="solo"
                  flat
                  color="black"
                  persistent-hint
                  prepend-icon="mdi-calendar"
                  readonly
                  v-bind="props"
                />
              </template>

              <v-date-picker
                v-model="endDate"
                color="red-darken-3"
                :min="startDate || null"
                @update:model-value="endDateMenu = false"
              />
            </v-menu>
          </v-col>
        </v-row>
        <v-row class="justify-center align-center pb-4 px-2 flex-wrap ga-2">
          <v-btn
            dark
            color="grey-darken-4"
            class="mx-1"
            @click="clearSearchFields"
          >
            {{ $t("parts.advanced_search.clear") }}
            <v-icon end> mdi-notification-clear-all </v-icon>
          </v-btn>
          <v-btn dark color="grey-darken-4" class="mx-1" @click="searchMethod">
            {{ $t("parts.advanced_search.search") }}
            <v-icon end> mdi-magnify </v-icon>
          </v-btn>
        </v-row>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import getRequest from "../../api/getRequest";
import { useDisplay } from "vuetify";

export default {
  data: () => ({
    searchTypes: [
      { label: "Title", value: "title" },
      { lable: "Multiple Titles", value: "multipleTitles" },
      { label: "ID", value: "id" },
      { label: "Other", value: "other" },
    ],
    searchType: "title",
    advancedSearch: false,
    mobileAdvancedOpen: false,
    search: "",
    searchTitle: null,
    searchMultipleTitles: null,
    titleCode: null,
    titleCodes: [],
    titles: [],
    titleGroups: [],
    status: [1, 2, 2.1, 3, 4, 5, 6, 6.1, 7, 8, 8.1, 9, 0],
    statusItems: [
      { title: "Entered", value: 1 },
      { title: "Returned-Sales", value: 2 },
      { title: "Returned-Shop", value: 2.1 },
      { title: "Re-Entered", value: 3 },
      { title: "Found", value: 4 },
      { title: "Sold", value: 5 },
      { title: "Lost", value: 6 },
      { title: "Lost-Shop", value: 6.1 },
      { title: "Damaged", value: 7 },
      { title: "Damaged-Sales", value: 8 },
      { title: "Damaged-Shop", value: 8.1 },
      { title: "Damaged-Lost", value: 9 },
      { title: "Recycled", value: 0 },
    ],
    startDate: "",
    endDate: "",
    startDateMenu: false,
    endDateMenu: false,
    loadingTitle: false,
    loadingMultipleTitles: false,
    menuOpen: false,
    selectingItem: false,
    menuMultipleOpen: false,
    selectingItemCodes: false,
    searchTimeout: null,
  }),
  computed: {
    display() {
      return useDisplay();
    },
    showAdvancedPanel() {
      return this.display.mdAndUp.value
        ? this.advancedSearch
        : this.mobileAdvancedOpen;
    },
  },
  watch: {
    search(value) {
      if (!value && this.advancedSearch === false) this.$emit("clear-search");
    },
    searchTitle(newValue) {
      if (this.selectingItem) {
        this.selectingItem = false;
        return;
      }
      if (!newValue) {
        this.titles = [];
        this.menuOpen = false;
        return;
      }
      clearTimeout(this.searchTimeout);

      this.searchTimeout = setTimeout(async () => {
        if (newValue) {
          this.loadingTitle = true;
          try {
            const response = await getRequest(
              "/info-car/parts",
              `title=${encodeURIComponent(newValue)}&language=${this.$store.getters.language}`,
            );
            await this.$nextTick();
            this.titles = response.parts || [];
          } catch (error) {
            this.titles = [];
          } finally {
            this.loadingTitle = false;
            this.menuOpen = this.titles.length > 0;
          }
        } else {
          this.titles = [];
          this.menuOpen = false;
        }
      }, 350); // 300ms delay
    },
    searchMultipleTitles(value) {
      if (value) {
        if (this.selectingItemCodes) {
          this.selectingItemCodes = false;
          return;
        }
        this.loadingMultipleTitles = true;
        getRequest(
          "/info-car/partgroups",
          `title=${value}&language=${this.$store.getters.language}`,
        )
          .then((response) => {
            this.titleGroups = response.partgroups;
          })
          .catch(() => {})
          .finally(() => {
            this.loadingMultipleTitles = false;
            if (!this.selectingItemCodes) {
              this.menuMultipleOpen = this.titleGroups.length > 0;
            }
          });
      } else {
        if (!this.titleCodes) {
          if (!this.advancedSearch) this.$emit("clear-search");
          this.titleCodes = [];
          this.menuMultipleOpen = false;
        } else {
          const selected = this.titleGroups.find(
            (t) => JSON.stringify(t.codes) === JSON.stringify(this.titleCodes),
          );
          if (selected) {
            this.selectingItemCodes = true; // prevent re-trigger
            this.searchMultipleTitles =
              selected.title?.[this.$store.getters.language] || "";
            this.menuMultipleOpen = false;
          }
        }
      }
    },
    advancedSearch(value) {
      if (!value) {
        this.mobileAdvancedOpen = false;
        this.status = [1, 2, 2.1, 3, 4, 5, 6, 6.1, 7, 8, 8.1, 9, 0];
        this.startDate = "";
        this.endDate = "";
      }
    },
  },
  mounted() {
    this.getLocale();
  },
  methods: {
    toggleMobileAdvanced() {
      this.mobileAdvancedOpen = !this.mobileAdvancedOpen;
      this.advancedSearch = this.mobileAdvancedOpen;
    },
    getLocale() {
      this.searchTypes[0].label = this.$t("parts.movements.title");
      this.searchTypes[1].label = this.$t("parts.movements.multiple_titles");
      this.searchTypes[2].label = this.$t("parts.movements.id");
      this.searchTypes[3].label = this.$t("parts.movements.other");
      this.statusItems[0].title = this.$t("parts.status.entered");
      this.statusItems[1].title = this.$t("parts.status.returned");
      this.statusItems[2].title = this.$t("parts.status.returned_shop");
      this.statusItems[3].title = this.$t("parts.status.re_entered");
      this.statusItems[4].title = this.$t("parts.status.found");
      this.statusItems[5].title = this.$t("parts.status.sold");
      this.statusItems[6].title = this.$t("parts.status.lost");
      this.statusItems[7].title = this.$t("parts.status.lost_shop");
      this.statusItems[8].title = this.$t("parts.status.damaged_inventory");
      this.statusItems[9].title = this.$t("parts.status.damaged_sales");
      this.statusItems[10].title = this.$t("parts.status.damaged_shop");
      this.statusItems[11].title = this.$t("parts.status.damaged_lost");
      this.statusItems[12].title = this.$t("parts.status.recycled");
    },
    formatDateToDisplay(date) {
      if (!date) return "";
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0"); // 0-indexed
      const year = d.getFullYear();
      return `${year}-${month}-${day}`;
    },
    formatDate(date) {
      if (!date) return "";
      moment.locale(localStorage.getItem("language") || "en");
      return moment(date).format("LLL");
    },
    formatMonthYear(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const month = String(date.getUTCMonth() + 1).padStart(2, "0"); // months are 0-indexed
      const year = date.getUTCFullYear();
      return `${month}/${year}`;
    },
    onGroupSelected(group) {
      if (group) {
        this.titleCodes = group.codes || [];
        this.selectingItemCodes = true;

        this.$nextTick(() => {
          setTimeout(() => {
            this.menuMultipleOpen = false;
          }, 100);
          this.searchMethod();
        });
      } else {
        this.titleCodes = [];
      }
    },
    searchMethod() {
      if (this.advancedSearch) {
        this.$emit("adv-search", {
          // ADVANCED
          type: this.searchType,
          searchField: this.search,
          titleCode: this.titleCode,
          titleCodes: this.titleCodes,
          advancedSearchFields: {
            status: this.status,
            startDate: this.startDate,
            endDate: this.endDate,
          },
        });
      } else if (
        this.search.length > 0 ||
        this.titleCode ||
        (this.titleCodes && this.titleCodes.length)
      ) {
        // REGULAR
        this.$emit("search", {
          type: this.searchType,
          searchField: this.search,
          titleCode: this.titleCode,
          titleCodes: this.titleCodes,
        });
      }
      this.menuOpen = false;
      this.menuMultipleOpen = false;
    },
    customFilter(item, queryText, itemText) {
      if (!queryText) return true;
      const searchText = queryText.toLowerCase();
      const language = this.$store.getters.language;

      // Check main title
      const mainTitle = itemText.raw?.title?.[language]?.toLowerCase() || "";
      if (mainTitle.includes(searchText)) return true;

      // Check related titles
      const relatedTitles = itemText.raw?.related || [];
      return relatedTitles.some((related) => {
        const relatedTitle = related?.title?.[language]?.toLowerCase() || "";
        return relatedTitle.includes(searchText);
      });
    },
    updateSearch() {
      // this.searchTitle = value;
    },
    selectCode(item) {
      this.titleCode = item.raw?.partCode || item.partCode || item.value;
      this.selectingItem = true;
      this.$nextTick(() => {
        this.searchMethod();
      });
    },
    clearSearchFields() {
      this.search = "";
      this.status = [1, 2, 2.1, 3, 4, 5, 6, 6.1, 7, 8, 8.1, 9, 0];
      this.startDate = "";
      this.endDate = "";
      this.$emit("clear-search");
    },
    highlight(title, search) {
      let result = title || "";
      if (search) {
        const index = title
          .toLocaleLowerCase()
          .indexOf(search.toLocaleLowerCase());
        // mark the text.
        if (index > -1)
          result = [
            title.slice(0, index),
            "<mark>",
            title.slice(index, index + search.length),
            "</mark>",
            title.slice(index + search.length),
          ].join("");
      }
      return `<span>${result}</span>`;
    },
  },
};
</script>
<style scoped>
.related-scroll {
  overflow-x: auto;
  white-space: nowrap;
  margin-top: -8px;
  max-width: 700px;
}

.related-scroll span {
  white-space: nowrap;
}

/* Top search strip (mirrors other part toolbars) */
.movements-toolbar-strip {
  position: relative;
  z-index: 1;
  width: 100%;
}

.movements-toolbar-root .min-width-0 {
  min-width: 0;
}

.movements-toolbar-root :deep(.movements-field-on-red .v-field) {
  min-width: 0;
}

@media (min-width: 960px) {
  .movements-toolbar-root .movements-toolbar-right {
    flex: 1 1 auto;
    min-width: 0;
    max-width: none;
  }

  .movements-toolbar-root .movements-toolbar-search-row {
    align-items: center;
  }

  .movements-toolbar-root .movements-toolbar-brand {
    max-width: min(38vw, 26rem);
  }
}

.movements-toolbar-root .movements-toolbar-type-select {
  width: min(100%, 11rem);
}

@media (min-width: 960px) {
  .movements-toolbar-root .movements-toolbar-type-select {
    width: 9.25rem;
  }
}

.movements-advanced-search-panel {
  overflow-x: hidden;
}
</style>
